import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileText, AlertCircleIcon, FileInput } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ImportBriefDialog } from "./ImportBriefDialog";

interface BriefHeaderProps {
  title: string;
  setTitle: (title: string) => void;
  onTitleSave: () => void;
  saving: boolean;
  viewMode: "guided" | "freeform";
  onFreeformSwitch: () => void;
  onGuidedSwitch: () => void;
  campaignId: string;
}

export function BriefHeader({
  title,
  setTitle,
  onTitleSave,
  saving,
  viewMode,
  onFreeformSwitch,
  onGuidedSwitch,
  campaignId,
}: BriefHeaderProps) {
  const [importOpen, setImportOpen] = useState(false);
  return (
    <div className="border-b h-12 p-1 pr-2 bg-background flex items-center justify-between">
      <Input
        value={title}
        defaultValue={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={onTitleSave}
        placeholder="Untitled brief . . ."
        className="font-medium h-10 border-none rounded-none shadow-none max-w-1/3"
      />
      <div className="flex items-center gap-2 ml-auto">
        {saving && (
          <div className="flex items-center gap-2 mr-3 text-muted-foreground text-xs">
            <Spinner className="size-3" />
            <span>Saving...</span>
          </div>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setImportOpen(true)}
        >
          <FileInput />
          Import from campaign
        </Button>
        {viewMode === "guided" ? (
          <Button variant="secondary" size="sm" onClick={onFreeformSwitch}>
            <FileText />
            Switch to Freeform Mode
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="secondary" size="sm">
                <FileText />
                Switch to Guided Mode
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Switch to Guided Mode?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your freeform content will be cleared. This action cannot be
                  undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Alert variant="destructive">
                <AlertTitle>
                  <AlertCircleIcon />
                  Your freeform content will be cleared.
                </AlertTitle>
                <AlertDescription>This action cannot be undone.</AlertDescription>
              </Alert>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction variant="neutral" onClick={onGuidedSwitch}>
                  <FileText />
                  Switch to Guided Mode
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <ImportBriefDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        currentCampaignId={campaignId}
      />
    </div>
  );
}
